import { useState } from "react";

import { ErrorComponent } from "@components/ErrorComponent";
import { Loader } from "@components/Loader";
import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import { EntryBody, Spinner } from "@entry/ui";
import { useBankLinkingFlow, useBankLinkToken } from "@hooks";
import { i18n, Translations } from "@i18n";
import { BankChip, ChipGrid, SectionLabel } from "./styles";
import { UnsupportedBankRouteComponentProps } from "./types";

const UNSUPPORTED_BANKS = [
  i18n.t(Translations.UnsupportedBankRouteUnsupportedBanksItem1),
  i18n.t(Translations.UnsupportedBankRouteUnsupportedBanksItem2),
  i18n.t(Translations.UnsupportedBankRouteUnsupportedBanksItem3),
  i18n.t(Translations.UnsupportedBankRouteUnsupportedBanksItem4),
  i18n.t(Translations.UnsupportedBankRouteUnsupportedBanksItem5),
  i18n.t(Translations.UnsupportedBankRouteUnsupportedBanksItem6),
  i18n.t(Translations.UnsupportedBankRouteUnsupportedBanksItem7),
  i18n.t(Translations.UnsupportedBankRouteUnsupportedBanksItem8),
  i18n.t(Translations.UnsupportedBankRouteUnsupportedBanksItem9),
  i18n.t(Translations.UnsupportedBankRouteUnsupportedBanksItem10),
  i18n.t(Translations.UnsupportedBankRouteUnsupportedBanksItem11),
  i18n.t(Translations.UnsupportedBankRouteUnsupportedBanksItem12),
];

export const UnsupportedBankRoute = () => {
  const { token, loading, error } = useBankLinkToken();

  if (loading) return <Loader />;
  if (error || !token) return <ErrorComponent />;

  return <UnsupportedBankRouteComponent token={token} />;
};

const UnsupportedBankRouteComponent = ({ token }: UnsupportedBankRouteComponentProps) => {
  // Plaid Link renders as an overlay; hide our content underneath it so
  // only the shell background shows behind the modal.
  const [plaidOpen, setPlaidOpen] = useState(false);

  const { open, ready, isSendingToken } = useBankLinkingFlow({
    token,
    onPlaidClose: () => setPlaidOpen(false),
  });

  const handleLinkBank = () => {
    if (ready) {
      setPlaidOpen(true);
      open();
    }
  };

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.UnsupportedBankRouteTitle),
    subtitle: i18n.t(Translations.UnsupportedBankRouteSubtitle),
    cta: {
      primary: {
        title: i18n.t(Translations.UnsupportedBankRoutePrimaryCTA),
        onClick: handleLinkBank,
        loading: isSendingToken,
      },
    },
  };

  return (
    <OnboardingLayout {...props}>
      {plaidOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spinner style={{ width: 34, height: 34, borderWidth: 3 }} />
        </div>
      )}

      <div style={{ visibility: plaidOpen ? "hidden" : "visible" }}>
        <SectionLabel>{i18n.t(Translations.UnsupportedBankRouteSupportedBanksTitle)}</SectionLabel>
        <Spacing size={SpacingSizes.XS} />
        <EntryBody style={{ fontSize: 14 }}>
          {i18n.t(Translations.UnsupportedBankRouteSupportedBanksSubtitle)}
        </EntryBody>
        <Spacing size={SpacingSizes.M} />

        <SectionLabel>
          {i18n.t(Translations.UnsupportedBankRouteUnsupportedBanksTitle)}
        </SectionLabel>
        <Spacing size={SpacingSizes.XS} />
        <ChipGrid>
          {UNSUPPORTED_BANKS.map((bank) => (
            <BankChip key={bank}>{bank}</BankChip>
          ))}
        </ChipGrid>
        <Spacing size={SpacingSizes.L} />
      </div>
    </OnboardingLayout>
  );
};
